import { flowRight, debounce } from 'lodash';
import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl, defineMessages } from 'react-intl';
import { Link } from '@zocdoc/components/src/link';
import { Stars } from '@zocdoc/components/src/stars';
import { Button } from '@zocdoc/components/src/button';
import styled, { withTheme } from 'styled-components';
import { Row, breakpoint, GridSize } from '@zocdoc/components/src/grid';
import { coral, grey25, white } from '@zocdoc/branding/colors';
import { FontFamily } from '@zocdoc/components/src/typography';
import { Sections } from '../../constants';
import withLocation from '../../../../config/location/withLocation';
import InfoTooltip from '../../../../Shared/InfoTooltip/view';
import { isCommunity } from '../../../../Shared/utils/communityLabel';
import { LOCALIZED_STRINGS } from './constants';
import { EVENT_ACTION } from '../../../../EventTrackingConstants';
import { isProduction } from '../../../../utils/environment';
import { MetricsClickable, CustomMetrics } from '@zocdoc/frontend-metrics';

function getSpecialtiesWithRelevantFirst(relevantSpecialty, specialties) {
    if (relevantSpecialty && specialties) {
        const otherSpecialtyNames = specialties.filter(specialty => {
            return specialty.id && specialty.id !== relevantSpecialty.id;
        }).map(specialty => {
            return specialty.name;
        });
        return [relevantSpecialty.name].concat(otherSpecialtyNames).join(', ');
    }
}

const localizedTooltips = iBubblesText => defineMessages({
    [Sections.Name]: {
        id: 'Profile.Page.Components.Name.Tooltip',
        defaultMessage: iBubblesText[Sections.Name],
    },
});

const CDN_REGEX = /(d2t808ag5aqhkh|d1cesmq0xhh7we|dsw5h1xg5uvx)\.cloudfront\.net/;

export class BaseHeader extends PureComponent {
    static propTypes = {
        location: PropTypes.shape({
            hostname: PropTypes.string,
        }),
        provider: PropTypes.shape({
            averageRating: PropTypes.number,
            firstName: PropTypes.string,
            lastName: PropTypes.string,
            title: PropTypes.string,
            rating: PropTypes.number,
            relevantSpecialty: PropTypes.object,
            specialties: PropTypes.array,
            circlePictureUrl: PropTypes.string,
            squarePictureUrl: PropTypes.string,
            smallPictures: PropTypes.arrayOf(PropTypes.string),
            showPhotoWithCircleCss: PropTypes.bool,
            canDeletePreviewProfiles: PropTypes.bool,
            onDeletePreviewProvider: PropTypes.func,
            deletingPreviewProvider: PropTypes.bool,
            deletePreviewProviderFailed: PropTypes.bool,
            properties: PropTypes.array,
        }),
        shiftPictureUp: PropTypes.bool,
        showGallery: PropTypes.func,
        showReviewsLink: PropTypes.bool,
        showStars: PropTypes.bool,
        onReviewStarsClick: PropTypes.func,
        onReviewLinkClick: PropTypes.func,
        theme: PropTypes.shape({
            profile: PropTypes.shape({
                iBubblesText: PropTypes.object,
                showCommunityLabel: PropTypes.bool,
            }),
        }),
    }

    static defaultProps = {
        location: {},
        showStars: true,
    };

    render() {
        const {
            intl,
            provider,
            theme,
            shiftPictureUp,
        } = this.props;

        const { iBubblesText } = theme.profile;
        const allLocalizedTooltips = localizedTooltips(iBubblesText);

        let tooltip = null;
        if (allLocalizedTooltips[Sections.Name] && allLocalizedTooltips[Sections.Name].defaultMessage) {
            const tooltipText = intl.formatMessage(allLocalizedTooltips[Sections.Name]);
            tooltip = (
                <TitleTooltipWrapper>
                    <CustomMetrics>
                        {
                            ({ sendMetric }) => {
                                const onDisplayTooltip = debounce(() => sendMetric({
                                    component: 'IBubble',
                                    element: 'IBubble',
                                    action: 'Click Provider Name Info Bubble',
                                }),
                                3000,
                                {
                                    leading: true,
                                    trailing: false,
                                });

                                return (
                                    <InfoTooltip
                                        alt={tooltipText}
                                        onDisplayTooltip={onDisplayTooltip}
                                    >
                                        {tooltipText}
                                    </InfoTooltip>
                                );
                            }
                        }
                    </CustomMetrics>
                </TitleTooltipWrapper>
            );
        }

        const StyledHeader = shiftPictureUp ? ShiftedHeader : Header;

        return (
            <StyledHeader>
                {provider.isPreview ? this.renderPreviewPicture() : this.renderPicture()}

                <DoctorTitle>
                    <span
                        data-uem-id="provider-name"
                        itemProp="name"
                    >
                        {provider.approvedFullName}
                    </span>
                    {tooltip}
                </DoctorTitle>

                {this.maybeRenderRating()}

                <Specialties>
                    {getSpecialtiesWithRelevantFirst(provider.relevantSpecialty, provider.specialties)}
                </Specialties>

                {this.maybeRenderCommunityLabel()}

                {this.maybeRenderDeleteButton()}
            </StyledHeader>
        );
    }

    maybeRenderCommunityLabel() {
        const {
            provider,
            showCommunityLabel,
        } = this.props;

        if (showCommunityLabel && isCommunity(provider)) {
            return (
                <CommunityLabel>
                    COMMUNITY
                </CommunityLabel>
            );
        }
    }

    renderPreviewPicture() {
        const { location: { hostname }, provider } = this.props;
        const circlePicture = provider.circlePictureUrl || '';
        const squarePicture = provider.squarePictureUrl || '';

        const PROD = isProduction(hostname);

        let mainPicture = provider.showPhotoWithCircleCss
            ? squarePicture.replace('mediumfixed', 'zoom')
            : circlePicture.replace('medium', 'large');

        if (!PROD) {
            // evaluating imgix, not meant for prod at this time
            mainPicture = mainPicture.replace(CDN_REGEX, 'zocdoc.imgix.net');
        }

        return (
            <PictureRow>
                <PictureFrame isPreview={provider.isPreview}>
                    <PictureSpacer />
                    <picture>
                        {!PROD ? <source
                            srcSet={`${mainPicture}?&fm=webp`}
                            type="image/webp"
                        /> : null}
                        <Picture
                            alt={provider.approvedFullName}
                            itemProp="image"
                            src={mainPicture}
                        />
                    </picture>
                </PictureFrame>
            </PictureRow>
        );
    }

    renderPicture() {
        const {
            location: { hostname },
            provider,
            showGallery,
            shiftPictureUp,
        } = this.props;

        const circlePicture = provider.circlePictureUrl || '';
        const squarePicture = provider.squarePictureUrl || '';

        const PROD = isProduction(hostname);

        let mainPicture = provider.showPhotoWithCircleCss
            ? squarePicture.replace('mediumfixed', 'zoom')
            : circlePicture.replace('medium', 'large');

        if (!PROD) {
            // evaluating imgix, not meant for prod at this time
            mainPicture = mainPicture.replace(CDN_REGEX, 'zocdoc.imgix.net');
        }

        const providerPicture = (
            <PictureFrame isPreview={provider.isPreview}>
                <PictureSpacer />
                <picture>
                    {!PROD ? <source
                        srcSet={`${mainPicture}?&fm=webp`}
                        type="image/webp"
                    /> : null}
                    <Picture
                        alt={provider.approvedFullName}
                        data-action-name={EVENT_ACTION.CLICK_PROVIDER_PICTURE}
                        data-test="show-gallery"
                        itemProp="image"
                        onClick={showGallery}
                        src={mainPicture}
                    />
                </picture>
            </PictureFrame>
        );

        // White border around shifted picture
        const styledPicture = shiftPictureUp ? (
            <PictureFrameShiftedWrapper>
                {providerPicture}
            </PictureFrameShiftedWrapper>
        ) : providerPicture;

        return (
            <PictureRow>
                {styledPicture}
                {provider.smallPictures && provider.smallPictures.length > 0 && (
                    <PictureCaption
                        data-action-name={EVENT_ACTION.CLICK_ADDITIONAL_PICTURE_CAPTION_LINK}
                        data-test="show-more-pictures"
                        onClick={showGallery}
                    >
                        <FormattedMessage
                            {...LOCALIZED_STRINGS.MainPictureCaption}
                            values={{ count: provider.smallPictures.length }}
                        />
                    </PictureCaption>
                )}
            </PictureRow>
        );
    }

    maybeRenderRating() {
        const {
            onReviewStarsClick,
            provider,
            showStars,
        } = this.props;

        const rating = provider.averageRating || 0;

        if (!showStars || provider.isPreview || rating === 0) {
            return null;
        } else {
            return (
                <DoctorRatingContainer>
                    <MetricsClickable
                        action="Click Review Stars"
                        element="ReviewStars"
                        eventDetails={{ AverageRatingShown: rating }}
                    >
                        <DoctorRating
                            data-test="header-rating-stars"
                            onClick={onReviewStarsClick}
                            rating={rating}
                        />
                    </MetricsClickable>
                    {this.maybeRenderRatingCaption()}
                </DoctorRatingContainer>
            );
        }
    }

    maybeRenderRatingCaption() {
        const {
            onReviewLinkClick,
            showReviewsLink,
            showReviewCount,
            provider,
        } = this.props;

        if (showReviewCount) {
            return (
                <MetricsClickable
                    action="Click Review Link"
                    element="ReviewLink"
                >
                    <RatingCaptionWrapper>
                        <ReviewCount
                            data-test="header-review-link"
                            onClick={onReviewLinkClick}
                            style="btn-link"
                        >
                            <FormattedMessage
                                {...LOCALIZED_STRINGS.ReviewsCount}
                                values={{ numberOfReviews: provider.reviewCount }}
                            />
                        </ReviewCount>
                    </RatingCaptionWrapper>
                </MetricsClickable>
            );
        } else if (showReviewsLink) {
            return (
                <MetricsClickable
                    action="Click Review Link"
                    element="ReviewLink"
                >
                    <RatingCaptionWrapper>
                        <ReadReviewsLink
                            data-test="header-review-link"
                            onClick={onReviewLinkClick}
                            style="btn-link"
                        >
                            <FormattedMessage {...LOCALIZED_STRINGS.ReadReviewsLink} />
                        </ReadReviewsLink>
                    </RatingCaptionWrapper>
                </MetricsClickable>
            );
        }
    }

    maybeRenderDeleteButton = () => {
        const {
            provider,
            deletingPreviewProvider,
            deletePreviewProviderFailed,
            canDeletePreviewProfiles,
            onDeletePreviewProvider,
        } = this.props;

        if (provider.isPreview && canDeletePreviewProfiles) {
            return (
                <DeleteButtonContainer>
                    <Button
                        data-test="preview-profile-delete-button"
                        disabled={deletingPreviewProvider}
                        onClick={onDeletePreviewProvider}
                    >
                        Delete Doctor
                    </Button>
                    {deletePreviewProviderFailed ? (
                        <div>
                            <DeletePreviewProviderError data-test="delete-preview-provider-error">
                                Failed to delete preview provider...
                            </DeletePreviewProviderError>
                        </div>
                    ) : undefined}
                </DeleteButtonContainer>
            );
        }
    }
}

const Header = styled(Row)`
    background-color: ${white};
    padding: 16px 30px;
    text-align: center;
    width: 100%;

    ${breakpoint.small} {
        padding: 30px;
    }

    ${breakpoint.medium} {
        padding: 28px 30px;
    }
`;

const ShiftedHeader = styled(Row)`
    background-color: transparent;
    padding: 0px 30px 16px;
    position: relative;
    text-align: center;
    top: -32px;
    margin-bottom: -32px;
    width: 100%;

    ${breakpoint.small} {
        background-color: ${white};
        padding: 30px;
        top: 0;
        margin-bottom: 0;
    }

    ${breakpoint.medium} {
        padding: 28px 30px;
    }
`;

const PictureRow = styled(Row)`
    position: relative;
`;

const Picture = styled.img`
    min-width: 96px;
    margin: -1px;
    position: absolute;
    top: 0;
    left: 0;

    ${breakpoint.small} {
        min-width: 146px;
    }
`;

const PictureFrame = styled.div`
    border-radius: 50%;
    border: 3px solid ${grey25};
    cursor: ${props => props.isPreview ? '' : 'pointer'};
    display: block;
    margin-bottom: 4px;
    margin-left: auto;
    margin-right: auto;
    max-height: 100px;
    max-width: 100px;
    overflow: hidden;
    position: relative;

    ${breakpoint.small} {
        margin-bottom: 10px;
        margin-top: 6px;
        max-height: 150px;
        max-width: 150px;
    }
`;

const PictureFrameShiftedWrapper = styled.div`
    border-radius: 50%;
    border: 8px solid white;
    display: block;
    margin-bottom: -8x;
    margin-left: auto;
    margin-right: auto;
    max-width: 116px;

    ${breakpoint.small} {
        border: none;
        border-radius: initial;
        margin-bottom: 0;
        max-width: initial;
    }
`;

const PictureSpacer = styled.div`
    margin-top: 100%;
`;

const PictureCaption = styled(Link)`
    font: 14px/15px ${FontFamily.SharpSansSemibold};
    bottom: 0;
    color: ${p => p.theme.profile.linkColor};
    cursor: pointer;
    display: none;
    left: 50%;
    position: absolute;
    transform: translate(56px, -11px);

    ${breakpoint.small} {
        display: block;
    }

    ${breakpoint.medium} {
        font: 16px/15px ${FontFamily.SharpSansSemibold};
    }
`;

const DoctorTitle = styled.h1`
    color: ${p => p.theme.profile.textColor};
    font: 24px/${7 * GridSize}px ${FontFamily.SharpSansBold};
    margin-top: 4px;

    ${breakpoint.small} {
        font: 48px/${11 * GridSize}px ${FontFamily.SharpSansBold};
        margin-top: 4px;
    }

    ${breakpoint.medium} {
        margin-top: 10px;
    }
`;

const DoctorRatingContainer = styled(Row)`
    font-size: 16px;
    margin-top: 8px;
    position: relative;

    ${breakpoint.small} {
        display: inline-block;
        font-size: 22px;
        margin-top: 20px;
    }
`;

const RatingCaptionWrapper = styled.div`
    padding: 0;

    ${breakpoint.small} {
        margin-left: 11px;
        position: absolute;
        left: 100%;
        top: 50%;
        transform: translateY(-50%);
        white-space: nowrap;
    }
`;

const ReviewCount = styled(Button)`
    border-bottom: 1px dotted;
    line-height: 1;
    padding: 0;
    font: 13px/16px ${FontFamily.SharpSansSemibold};
    padding-top: 6px;
    margin-bottom: 4px;

    ${breakpoint.small} {
        font: 18px/20px ${FontFamily.SharpSansSemibold};
        margin-bottom: 0px;
    }

    &:hover {
        border-bottom: 1px solid;
        text-decoration: none;
    }

`;

const ReadReviewsLink = styled(ReviewCount)`
    font-size: 16px;
    line-height: 16px;
    margin-bottom: 3px;
    ${breakpoint('xsmall', { down: true })} {
        display: none;
    }
`;

const Specialties = styled(Row.withComponent('h2'))`
    font: 15px/24px ${FontFamily.SharpSansSemibold};
    color: ${p => p.theme.profile.textColor};
    margin-bottom: 8px;
    margin-top: 6px;

    ${breakpoint.small} {
        font: 18px/24px ${FontFamily.SharpSansSemibold};
        margin-top: 20px;
    }
`;

const TitleTooltipWrapper = styled.span`
    ${breakpoint.medium} {
        line-height: 12px;
        position: relative;
        top: -6px;
    }
`;

const DoctorRating = styled(Stars)`
    cursor: pointer;
    vertical-align: middle;
`;

const DeleteButtonContainer = styled.div`
    width: 100%;
`;

const DeletePreviewProviderError = styled.p`
    font: 13px/18px ${FontFamily.SharpSansMedium};
    color: ${coral};
`;

const CommunityLabel = styled(Row)`
    font: 14px ${FontFamily.SharpSansBold};
    margin-top: 8px;
`;

export default flowRight(
    injectIntl,
    withTheme,
    withLocation,
)(BaseHeader);
